import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Search, Filter, Trash2, Download, FileText, Send, X, ChevronLeft, Clock, Check, AlertTriangle, RefreshCw, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Workplace Issue', 'Salary', 'Harassment', 'Suggestion', 'Other'];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
  replied: { label: 'Replied', color: 'bg-violet-100 text-violet-700', icon: Check },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-500', icon: Check },
};

const CATEGORY_CONFIG = {
  'Workplace Issue': { color: 'bg-amber-50 text-amber-700 border-amber-200' },
  'Salary': { color: 'bg-green-50 text-green-700 border-green-200' },
  'Harassment': { color: 'bg-red-50 text-red-700 border-red-200' },
  'Suggestion': { color: 'bg-sky-50 text-sky-700 border-sky-200' },
  'Other': { color: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const GrievanceManager = () => {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGrievance, setActiveGrievance] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', search: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';

  const loadGrievances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      const res = await axios.get(`${API}/api/grievance-manager/all?${params}`, { headers });
      setGrievances(res.data.grievances || []);
    } catch (err) {
      console.error('Failed to load grievances:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.category]);

  useEffect(() => { loadGrievances(); }, [loadGrievances]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grievance permanently?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/grievance-manager/${id}`, { headers });
      setGrievances(prev => prev.filter(g => g.id !== id));
      if (activeGrievance?.id === id) setActiveGrievance(null);
    } catch (err) {
      alert('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadSingle = async (id) => {
    try {
      const res = await axios.get(`${API}/api/grievance-manager/download/single/${id}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `grievance_${id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await axios.get(`${API}/api/grievance-manager/download/all`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_grievances_report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
    finally { setDownloadingAll(false); }
  };

  const filtered = grievances.filter(g => {
    if (filters.search) {
      const s = filters.search.toLowerCase();
      return (
        g.employee_name?.toLowerCase().includes(s) ||
        g.employee_code?.toLowerCase().includes(s) ||
        g.message?.toLowerCase().includes(s) ||
        g.department?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const counts = {
    all: grievances.length,
    open: grievances.filter(g => g.status === 'open').length,
    replied: grievances.filter(g => g.status === 'replied').length,
    closed: grievances.filter(g => g.status === 'closed').length,
  };

  if (activeGrievance) {
    return (
      <GrievanceDetail
        grievanceId={activeGrievance}
        headers={headers}
        isViewer={isViewer}
        onBack={() => { setActiveGrievance(null); loadGrievances(); }}
        onDelete={handleDelete}
        onDownload={handleDownloadSingle}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="grievance-manager">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grievances</h1>
          <p className="text-sm text-gray-500 mt-1">Employee messages and concerns</p>
        </div>
        {grievances.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadAll}
            disabled={downloadingAll}
            data-testid="download-all-grievances-btn"
          >
            {downloadingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download All PDF
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: counts.all, color: 'bg-gray-50 border-gray-200' },
          { label: 'Open', value: counts.open, color: 'bg-blue-50 border-blue-200' },
          { label: 'Replied', value: counts.replied, color: 'bg-violet-50 border-violet-200' },
          { label: 'Closed', value: counts.closed, color: 'bg-gray-50 border-gray-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, code, message..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-9"
            data-testid="grievance-search"
          />
        </div>
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="grievance-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="grievance-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No grievances found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(g => {
            const sc = STATUS_CONFIG[g.status] || STATUS_CONFIG.open;
            const cc = CATEGORY_CONFIG[g.category] || CATEGORY_CONFIG.Other;
            return (
              <Card
                key={g.id}
                className="hover:shadow-md transition cursor-pointer"
                onClick={() => setActiveGrievance(g.id)}
                data-testid={`grievance-card-${g.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{g.employee_name}</p>
                          {g.employee_code && <span className="text-xs text-gray-400">{g.employee_code}</span>}
                          {g.is_anonymous !== false && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-violet-50 text-violet-500 border border-violet-200">Anonymous</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] px-2 py-0 ${sc.color}`}>{sc.label}</Badge>
                          <Badge variant="outline" className={`text-[10px] px-2 py-0 ${cc.color}`}>{g.category}</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{g.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>{g.department}</span>
                          <span>{new Date(g.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          {g.reply_count > 0 && <span className="text-violet-500">{g.reply_count} {g.reply_count === 1 ? 'reply' : 'replies'}</span>}
                        </div>
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadSingle(g.id)} data-testid={`download-grievance-${g.id}`}>
                            <FileText className="h-3.5 w-3.5 text-gray-400" />
                          </Button>
                          {!isViewer && (
                            <Button
                              variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => handleDelete(g.id)}
                              disabled={deleting === g.id}
                              data-testid={`delete-grievance-${g.id}`}
                            >
                              {deleting === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-400" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Detail / Chat View ────────────────── */
const GrievanceDetail = ({ grievanceId, headers, isViewer, onBack, onDelete, onDownload }) => {
  const [grievance, setGrievance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/grievance-manager/${grievanceId}`, { headers });
      setGrievance(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [grievanceId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [grievance?.replies]);

  const sendReply = async () => {
    if (!reply.trim() || isViewer) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/grievance-manager/${grievanceId}/reply`, { message: reply.trim() }, { headers });
      setReply('');
      load();
    } catch { alert('Failed to send reply'); }
    finally { setSending(false); }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await axios.patch(`${API}/api/grievance-manager/${grievanceId}/close`, {}, { headers });
      load();
    } catch { alert('Failed to close'); }
    finally { setClosing(false); }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await axios.patch(`${API}/api/grievance-manager/${grievanceId}/reopen`, {}, { headers });
      load();
    } catch { alert('Failed to reopen'); }
    finally { setReopening(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
      </div>
    );
  }

  if (!grievance) return <p className="text-center text-gray-400 py-16">Grievance not found</p>;

  const isClosed = grievance.status === 'closed';
  const sc = STATUS_CONFIG[grievance.status] || STATUS_CONFIG.open;
  const cc = CATEGORY_CONFIG[grievance.category] || CATEGORY_CONFIG.Other;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="grievance-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="grievance-back-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">{grievance.employee_name}</h2>
            <span className="text-sm text-gray-400">{grievance.employee_code}</span>
            <Badge className={`text-[10px] px-2 py-0 ${sc.color}`}>{sc.label}</Badge>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <Badge variant="outline" className={`text-[10px] px-2 py-0 ${cc.color}`}>{grievance.category}</Badge>
            <span>{grievance.department}</span>
            <span>{grievance.position}</span>
            <span>{new Date(grievance.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onDownload(grievanceId)} data-testid="detail-download-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          {!isViewer && !isClosed && (
            <Button variant="outline" size="sm" onClick={handleClose} disabled={closing} data-testid="close-grievance-btn">
              {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Close</>}
            </Button>
          )}
          {!isViewer && isClosed && (
            <Button variant="outline" size="sm" onClick={handleReopen} disabled={reopening} data-testid="reopen-grievance-btn">
              {reopening ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-1" /> Reopen</>}
            </Button>
          )}
          {!isViewer && (
            <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => onDelete(grievanceId)} data-testid="detail-delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Chat area */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-320px)] overflow-y-auto p-6 space-y-4" ref={scrollRef}>
            {/* Original message (from employee) */}
            <div className="flex justify-start">
              <div className="max-w-[70%] bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3">
                <p className="text-[10px] font-semibold text-gray-500 mb-1">Employee</p>
                <p className="text-sm text-gray-800 leading-relaxed">{grievance.message}</p>
                <p className="text-[10px] text-gray-400 mt-2">
                  {new Date(grievance.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {' '}{new Date(grievance.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                </p>
              </div>
            </div>

            {/* Replies */}
            {(grievance.replies || []).map(r => {
              const isAdmin = r.sender_type === 'admin';
              return (
                <div key={r.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                    isAdmin
                      ? 'bg-violet-500 text-white rounded-tr-md'
                      : 'bg-gray-100 rounded-tl-md'
                  }`}>
                    <p className={`text-[10px] font-semibold mb-1 ${isAdmin ? 'text-violet-100' : 'text-gray-500'}`}>
                      {isAdmin ? (r.sender_name || 'HR Admin') : 'Employee'}
                    </p>
                    <p className={`text-sm leading-relaxed ${isAdmin ? '' : 'text-gray-800'}`}>{r.message}</p>
                    <p className={`text-[10px] mt-2 ${isAdmin ? 'text-violet-200' : 'text-gray-400'}`}>
                      {new Date(r.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {' '}{new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </p>
                  </div>
                </div>
              );
            })}

            {isClosed && (
              <div className="text-center py-3">
                <Badge className="text-xs bg-gray-100 text-gray-500">Conversation closed</Badge>
              </div>
            )}
          </div>

          {/* Reply box */}
          {!isClosed && !isViewer && (
            <div className="border-t p-4 flex items-end gap-3">
              <Textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder="Type your reply to the employee..."
                rows={2}
                className="flex-1 min-h-[48px] max-h-[120px] resize-none"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                data-testid="admin-reply-input"
              />
              <Button
                className="bg-violet-600 hover:bg-violet-700 h-12 px-5"
                onClick={sendReply}
                disabled={!reply.trim() || sending}
                data-testid="admin-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GrievanceManager;
